/**
 * The values a loaded study yields: the scene origin, the femoral bases the stem is placed
 * against, the native measurements panel and the segmentation fit scores.
 *
 * Split out of `loading.ts`, which orchestrates the requests. These derive from data that is
 * already in hand and reach no network, which is what makes them unit-testable — `loading.ts`
 * cannot be, because its API imports sit in the MSW module graph that `vi.mock` cannot intercept.
 * Keeping the two apart means a change to the arithmetic is provable without an integration run.
 */

import { Vector3 } from 'three';
import type { ApiStudyFitting } from '@/api/study/studyFitting';
import type { ApiStudyLandmarks } from '@/api/study/studyLandmarks';
import type { ApiStudyMeasurements } from '@/api/study/studyMeasurements';
import { findDiceScore } from '@/domain/anatomy/fitting';
import { findLandmark } from '@/domain/anatomy/landmarks';
import LPS, { type LpsVectors } from '@/domain/anatomy/LPS';
import {
    type AxisMeasurement,
    findAngleMeasurement,
    findAxisMeasurement,
    findDistanceMeasurement,
    tryFindDistanceMeasurement,
} from '@/domain/anatomy/measurements';
import { nativeAcetabularAngles } from '@/domain/anatomy/pelvis/nativeAcetabularAngles';
import type { SideId } from '@/domain/anatomy/side';
import type { NumberArray3 } from '@/domain/geometry/apiVector';
import {
    formatBasis,
    formatDegrees,
    formatFloat,
    joinIndented,
} from '@/domain/geometry/formatMath';
import { areCollinear, orthogonalUnit, vector3 } from '@/domain/geometry/vector3';
import { assertDefined } from '@/util/assert';
import { taggedLogger } from '@/util/taggedLogger';
import type { FemoralFeatures, NativeMeasurements } from '@/views/3d-planner/store/plannerState';
import { logValidation } from './logValidation';

// The same tag loading.ts uses: these values are part of the case-load story, and a
// reader filtering the console for it should not lose half of it to a split.
const log = taggedLogger('case-load');

/**
 * Calculate the centre-point for a hip case. This is a point in the centre of the pelvis between
 * the left and right asis.
 *
 * NOTE: also known as the scene 'origin'
 */
export function calculateSceneCentre({ groups }: ApiStudyLandmarks): Vector3 {
    const leftItem = findLandmark(groups, 'pelvis', 'lasis');
    const rightItem = findLandmark(groups, 'pelvis', 'rasis');
    return vector3(leftItem.coordinates).add(rightItem.coordinates).divideScalar(2);
}

/**
 * Create an LPS basis aligned to a femoral coordinate system:
 * - superior direction along the **proximal** shaft-axis
 * - left/right medial direction in the same plane as the neck-axis but orthogonal to the superior direction
 * - posterior direction orthogonal to the superior and medial directions
 *
 * This basis is used to position the stem.
 */
function calculateStemTranslationBasis(
    side: SideId,
    proximalShaftAxis: AxisMeasurement,
    neckAxis: AxisMeasurement,
): LpsVectors {
    const superior = vector3(proximalShaftAxis.value).normalize();
    const medial = vector3(neckAxis.value).projectOnPlane(superior).normalize();
    const left = side === 'right' ? medial : medial.clone().negate();
    const posterior = orthogonalUnit(superior, left);
    const result = { left, posterior, superior };

    logValidation(
        joinIndented(2)(['Stem-translation-basis:', ...formatBasis(result, { precision: 5 })]),
    );

    return { left, posterior, superior };
}

/**
 * Create an LPS basis aligned to a femoral coordinate system:
 * - superior direction corresponding to the femoral-shaft-axis.
 * - posterior direction orthogonal to the superior direction in the CT saggital plane
 *
 * This is intended to be functionally equivalent to the shaft-plane used to calculate
 * stem-anteversion on the server-side.
 */
function calculateFemoralShaftBasis(shaftAxis: AxisMeasurement): LpsVectors {
    const superior = vector3(shaftAxis.value).normalize();

    let approxPosterior: Vector3;
    if (areCollinear(superior, LPS.Posterior)) {
        log.warn('Femoral shaft axis is exactly in posterior direction');
        approxPosterior = LPS.Inferior;
    } else {
        approxPosterior = LPS.Posterior;
    }

    const left = orthogonalUnit(approxPosterior, superior);
    const posterior = orthogonalUnit(superior, left);
    const result = { left, posterior, superior };

    logValidation(
        joinIndented(2)(['Femoral-shaft-basis:', ...formatBasis(result, { precision: 5 })]),
    );

    return { left, posterior, superior };
}

/**
 * Extract femoral features from a set of study-measurements
 *
 * Returns null if the necessary measurements cannot be found, which may be the result of an old cases.
 */
export function findFemoralFeatures(
    side: SideId,
    { groups }: ApiStudyMeasurements,
): FemoralFeatures | null {
    try {
        const shaftAxis = findAxisMeasurement(
            groups,
            { bone: 'femur', side },
            'hip_femur_shaft_axis',
        );
        const proximalShaftAxis = findAxisMeasurement(
            groups,
            { bone: 'femur', side },
            'hip_femur_proximal_shaft_axis',
        );
        const neckAxis = findAxisMeasurement(
            groups,
            { bone: 'femur', side },
            'hip_femur_neck_axis',
        );
        const anteversion = findAngleMeasurement(
            groups,
            { bone: 'femur', side },
            'hip_femur_anteversion_angle',
        );

        const [anteversionCondylarAxis, anteversionNeckAxis] = assertDefined(
            anteversion.axes,
            'anteversion-axes',
        );

        return {
            shaftAxis,
            proximalShaftAxis,
            neckAxis,
            anteversionCondylar: assertDefined(
                anteversionCondylarAxis.value,
                'anteversion-condylar-axis',
            ),
            anteversionNeck: assertDefined(anteversionNeckAxis.value, 'anteversion-neck-axis'),
            stemTranslationBasis: calculateStemTranslationBasis(side, proximalShaftAxis, neckAxis),
            shaftBasis: calculateFemoralShaftBasis(shaftAxis),
        };
    } catch (error) {
        if (error instanceof Error) {
            log.warning(
                'Error getting femoral features needed for femoral alignment ' +
                    '(this may be an old case missing this data):\n%s',
                error.message,
            );
            return null;
        } else {
            throw error;
        }
    }
}

export function makeNativeMeasurements(
    side: SideId,
    { groups }: ApiStudyMeasurements,
    alignmentVectors: LpsVectors,
): NativeMeasurements {
    const { anteversion, inclination } = nativeAcetabularAngles(side, groups, alignmentVectors);
    const result = {
        hipLengthDifference: findDistanceMeasurement(
            groups,
            'pelvis',
            'hip_pelvis_leg_length_difference',
        ).value,
        femoralAnteversion: findAngleMeasurement(
            groups,
            { bone: 'femur', side },
            'hip_femur_anteversion_angle',
        ).value,
        acetabularAnteversion: anteversion,
        acetabularInclination: inclination,
    };

    logValidation(
        [
            'Native measurements:',
            `  hip-length-difference: ${formatFloat(result.hipLengthDifference)} mm`,
            `  femoral-anteversion: ${formatDegrees(result.femoralAnteversion)}`,
            `  acetabular-anteversion: ${formatDegrees(result.acetabularAnteversion)}`,
            `  acetabular-inclination: ${formatDegrees(result.acetabularInclination)}`,
        ].join('\n'),
    );

    return result;
}

export function findLtResectionPoint(
    side: SideId,
    { groups }: ApiStudyMeasurements,
): NumberArray3 | null {
    const measurement = tryFindDistanceMeasurement(
        groups,
        { bone: 'femur', side },
        'hip_femur_landmark_lesser_trochanter_resection',
    );
    return measurement?.points?.[0] ?? null;
}

export function makeFemurDiceScores(fitResults: ApiStudyFitting): {
    operativeFemurDiceScore: number | null;
    contralateralFemurDiceScore: number | null;
} | null {
    const result = {
        operativeFemurDiceScore: findDiceScore(fitResults.fit, 'operative-femur'),
        contralateralFemurDiceScore: findDiceScore(fitResults.fit, 'contralateral-femur'),
    };

    if (result.operativeFemurDiceScore == null && result.contralateralFemurDiceScore == null) {
        return null; // Return null if no dice scores exist
    }

    logValidation(
        [
            'Femur Dice Scores:',
            `  Operative Femur Dice Score: ${result.operativeFemurDiceScore}`,
            `  Contralateral Femur Dice Score: ${result.contralateralFemurDiceScore}`,
        ].join('\n'),
    );

    return result;
}
